import { FC, useEffect, useCallback, useRef } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { OnboardingHighlightOverlay } from './OnboardingHighlightOverlay'
import { OnboardingTooltip } from './OnboardingTooltip'
import dynamicPaperTutorialSelector from '@/redux/selectors/dynamic-paper-onboarding-tutorial'
import {
  advanceDynamicPaperTutorialStep,
  hideDynamicPaperTutorialInfoModal,
  setDynamicPaperTutorialHighlightedElement,
  setDynamicPaperTutorialCurrentStep,
  setDynamicPaperTutorialLoading,
  showDynamicPaperTutorialInfoModal,
  stopDynamicPaperTutorial,
} from '@/redux/slices/dynamic-paper-onboarding-tutorial'
import {
  DynamicPaperTutorialFlow,
  DynamicPaperTutorialStep,
  IDynamicPaperTutorialStepConfig,
} from '@/types/redux'

export const dynamicPaperTutorialStepConfigs: Record<
  DynamicPaperTutorialStep,
  IDynamicPaperTutorialStepConfig
> = {
  [DynamicPaperTutorialStep.MarkScheme]: {
    step: DynamicPaperTutorialStep.MarkScheme,
    title: 'Mark Scheme',
    description:
      "Compare your working with the mark scheme to see if you're on the right track to earn full marks.",
    highlightConfig: {
      elementId: 'dp-tutorial-mark-scheme-toggle',
    },
    nextStep: DynamicPaperTutorialStep.Input,
    actionRequired: false,
    autoAdvance: false,
  },
  [DynamicPaperTutorialStep.Input]: {
    step: DynamicPaperTutorialStep.Input,
    title: 'Write Your Solution',
    description:
      'Use this area to show your steps. Remember: Enter submits your work, while Shift + Enter adds a new line.',
    highlightConfig: {
      elementId: 'math-live-input',
    },
    nextStep: DynamicPaperTutorialStep.MathsKb,
    actionRequired: false,
    autoAdvance: false,
  },
  [DynamicPaperTutorialStep.NonMathInput]: {
    step: DynamicPaperTutorialStep.NonMathInput,
    title: 'Write Your Response',
    description:
      // 'Use this area to show your steps. Remember: Enter submits your work, while Shift + Enter adds a new line.',
      'Use this area to type your answer. You can format your text using Bold, Italic, or Headings to make your solution clear and structured.',
    highlightConfig: {
      elementId: 'dp-tutorial-non-math-input',
    },
    nextStep: null,
    actionRequired: false,
    autoAdvance: false,
  },
  [DynamicPaperTutorialStep.MathsKb]: {
    step: DynamicPaperTutorialStep.MathsKb,
    title: 'Maths Keyboard',
    description:
      'Click here to access fractions, roots, and other math symbols for your solution.',
    highlightConfig: {
      elementId: 'dp-tutorial-maths-kb-toggle',
    },
    nextStep: DynamicPaperTutorialStep.InputMode,
    actionRequired: false,
    autoAdvance: false,
  },
  [DynamicPaperTutorialStep.Scan]: {
    step: DynamicPaperTutorialStep.Scan,
    title: 'Scan Hand-written Work',
    description:
      'Prefer writing on paper? Scan this QR code with your phone to instantly snap a photo and upload your handwritten solution.',
    highlightConfig: {
      elementId: 'qr-code-popover',
    },
    nextStep: null,
    actionRequired: false,
    autoAdvance: false,
  },
  [DynamicPaperTutorialStep.Upload]: {
    step: DynamicPaperTutorialStep.Scan,
    title: 'Upload Image',
    description:
      'Already have a photo or a digital copy of your work? Upload it directly from your device to include it in your submission.',
    highlightConfig: {
      elementId: 'local-image-upload',
    },
    nextStep: null,
    actionRequired: false,
    autoAdvance: false,
  },
  [DynamicPaperTutorialStep.InputMode]: {
    step: DynamicPaperTutorialStep.InputMode,
    title: 'Text vs. Formula',
    description:
      'Switch modes to toggle between writing plain text explanations and LaTeX math formulas.',
    highlightConfig: {
      elementId: 'dp-tutorial-input-mode-switcher',
    },
    nextStep: DynamicPaperTutorialStep.GeneralFeedback,
    actionRequired: false,
    autoAdvance: false,
  },
  [DynamicPaperTutorialStep.GeneralFeedback]: {
    step: DynamicPaperTutorialStep.GeneralFeedback,
    title: 'Deep AI Feedback',
    description:
      'Summarize your results for the entire paper and work on mistakes. Axi, our AI tutor, will highlight your weak spots and help you upgrade your knowledge.',
    highlightConfig: {
      elementId: 'general-feedback-btn',
    },
    nextStep: null,
    actionRequired: false,
    autoAdvance: false,
  },
  [DynamicPaperTutorialStep.Welcome]: {
    step: DynamicPaperTutorialStep.Welcome,
    title: 'Welcome',
    description:
      'Quick tour: we will show you where to answer questions, switch maths tools, open the mark scheme, and track progress.',
    nextButtonText: 'Start',
    highlightConfig: {
      elementId: 'dp-tutorial-welcome',
    },
    skipButtonText: 'Skip Tutorial',
    nextStep: null,
    actionRequired: false,
    autoAdvance: false,
  },
}

export function generateDynamicPaperTutorialSteps(
  stepsInOrder: Partial<
    Record<DynamicPaperTutorialStep, Partial<IDynamicPaperTutorialStepConfig>>
  >,
): Partial<Record<DynamicPaperTutorialStep, IDynamicPaperTutorialStepConfig>> {
  const stepEntries = Object.entries(stepsInOrder)

  return stepEntries.reduce(
    (
      acc: Partial<
        Record<DynamicPaperTutorialStep, IDynamicPaperTutorialStepConfig>
      >,
      [step, additionalConfig],
      index,
    ) => {
      acc[step] = {
        ...dynamicPaperTutorialStepConfigs[step],
        ...additionalConfig,
        nextStep: stepEntries[index + 1]?.[0] ?? null,
      }

      return acc
    },
    {},
  )
}

interface DynamicPaperTutorialProps {
  firstStep: DynamicPaperTutorialStep
  onComplete?: () => Promise<void>
  flow?: DynamicPaperTutorialFlow
  stepConfigs?: Partial<
    Record<DynamicPaperTutorialStep, IDynamicPaperTutorialStepConfig>
  >
  enableWelcomeModal?: boolean
}

export const DynamicPaperTutorial: FC<DynamicPaperTutorialProps> = ({
  firstStep,
  onComplete,
  flow = DynamicPaperTutorialFlow.Regular,
  stepConfigs = dynamicPaperTutorialStepConfigs,
  enableWelcomeModal = false,
}) => {
  const dispatch = useDispatch()
  const {
    isActive,
    currentStep,
    completedSteps,
    highlightedElementId,
    showInfoModal: showModal,
    infoModalContent,
    isLoading,
  } = useSelector(dynamicPaperTutorialSelector)

  const didInitWelcomeRef = useRef(false)

  useEffect(() => {
    if (!isActive || !enableWelcomeModal) {
      if (!isActive) {
        didInitWelcomeRef.current = false
      }
      return
    }

    if (didInitWelcomeRef.current) {
      return
    }

    if (completedSteps.length > 0) {
      didInitWelcomeRef.current = true
      return
    }

    didInitWelcomeRef.current = true
    dispatch(
      setDynamicPaperTutorialCurrentStep(DynamicPaperTutorialStep.Welcome),
    )
  }, [completedSteps.length, dispatch, enableWelcomeModal, isActive])

  useEffect(() => {
    if (!isActive) {
      return
    }

    if (
      enableWelcomeModal &&
      completedSteps.length === 0 &&
      currentStep === firstStep
    ) {
      return
    }

    // Optional Welcome step: no highlight, centered modal.
    if (
      enableWelcomeModal &&
      currentStep === DynamicPaperTutorialStep.Welcome
    ) {
      dispatch(setDynamicPaperTutorialHighlightedElement(null))
      dispatch(
        showDynamicPaperTutorialInfoModal({
          title: 'Welcome',
          description:
            'Quick tour: we will show you where to answer questions, switch maths tools, open the mark scheme, and track progress.',
          nextButtonText: 'Start',
          skipButtonText: 'Skip Tutorial',
        }),
      )
      return
    }

    const config = stepConfigs[currentStep]
    if (!config) {
      return
    }

    const modalContent = {
      title: config.title,
      description: config.description,
      nextButtonText: config.nextStep
        ? config.nextButtonText || 'Ok'
        : 'Complete',
      skipButtonText: config.skipButtonText || 'Skip Tutorial',
    }

    if (config.highlightConfig && config.highlightConfig.elementId) {
      dispatch(hideDynamicPaperTutorialInfoModal())
      dispatch(setDynamicPaperTutorialHighlightedElement(null))

      const elementRenderDelay = 500
      const elementId = config.highlightConfig.elementId

      setTimeout(() => {
        dispatch(setDynamicPaperTutorialHighlightedElement(elementId))
        dispatch(showDynamicPaperTutorialInfoModal(modalContent))
      }, elementRenderDelay)
    } else {
      dispatch(setDynamicPaperTutorialHighlightedElement(null))
      dispatch(showDynamicPaperTutorialInfoModal(modalContent))
    }

    if (config.autoAdvance) {
      const timer = setTimeout(() => {
        const nextConfig = stepConfigs[currentStep]
        if (nextConfig?.nextStep) {
          dispatch(advanceDynamicPaperTutorialStep(nextConfig.nextStep))
        }
      }, 3000)

      return () => clearTimeout(timer)
    }
  }, [
    isActive,
    currentStep,
    completedSteps.length,
    dispatch,
    enableWelcomeModal,
    stepConfigs,
    firstStep,
  ])

  const handleComplete = useCallback(async () => {
    try {
      dispatch(setDynamicPaperTutorialLoading(true))
      dispatch(stopDynamicPaperTutorial())

      if (onComplete) {
        await onComplete()
      }
    } finally {
      dispatch(setDynamicPaperTutorialLoading(false))
    }
  }, [dispatch, onComplete])

  const handleNextStep = useCallback(async () => {
    if (
      enableWelcomeModal &&
      currentStep === DynamicPaperTutorialStep.Welcome
    ) {
      dispatch(setDynamicPaperTutorialCurrentStep(firstStep))

      return
    }

    const config = stepConfigs[currentStep]

    if (!config?.nextStep) {
      await handleComplete()
      return
    }

    dispatch(advanceDynamicPaperTutorialStep(config.nextStep))
  }, [
    currentStep,
    dispatch,
    enableWelcomeModal,
    firstStep,
    handleComplete,
    stepConfigs,
  ])

  const handleSkipTutorial = useCallback(async () => {
    try {
      dispatch(setDynamicPaperTutorialLoading(true))
      dispatch(stopDynamicPaperTutorial())

      if (onComplete) {
        await onComplete()
      }
    } finally {
      dispatch(setDynamicPaperTutorialLoading(false))
    }
  }, [dispatch, onComplete])

  useEffect(() => {
    if (!isActive || !highlightedElementId) {
      return
    }

    const config = stepConfigs[currentStep]
    if (!config?.actionRequired) return

    const handleElementClick = (event: Event) => {
      const target = event.target as HTMLElement
      const highlightedElement = document.getElementById(highlightedElementId)

      if (
        highlightedElement &&
        (highlightedElement.contains(target) || highlightedElement === target)
      ) {
        setTimeout(() => {
          handleNextStep()
        }, 300)
      }
    }

    document.addEventListener('click', handleElementClick, true)
    return () => {
      document.removeEventListener('click', handleElementClick, true)
    }
  }, [isActive, highlightedElementId, currentStep, handleNextStep, stepConfigs])

  if (!isActive) {
    return null
  }

  return (
    <>
      <OnboardingHighlightOverlay
        targetElementId={highlightedElementId}
        isActive={isActive}
        padding={8}
        zIndex={180} // sync with lesson explorer sidebar & sidebar-toggle-btn
        useBoxShadow={stepConfigs[currentStep]?.highlightConfig?.useBoxShadow}
      />
      <OnboardingTooltip
        targetElementId={highlightedElementId}
        isOpen={showModal}
        content={infoModalContent}
        onNext={infoModalContent?.nextButtonText ? handleNextStep : undefined}
        onSkip={
          infoModalContent?.skipButtonText ? handleSkipTutorial : undefined
        }
        onClose={handleSkipTutorial}
        isLoading={isLoading}
        showBackdrop={false}
        zIndex={181} // always higher than OnboardingHighlightOverlay
        overlayPadding={8}
        preferredTooltipPlacement={
          stepConfigs[currentStep]?.preferredTooltipPlacement
        }
      />
    </>
  )
}
